import {
  ReservationQuestionDto,
  ReservationQuestionOptionDto,
} from "@/api/generated/generatedTypes";
import { EventQuestionSlug } from "@/api/reservation/dto";
import { Button, SimpleGrid, Stack, Text, Textarea, useMantineTheme } from "@mantine/core";

interface EventAnswerOptionsListProps {
  selectedOptions?: string | string[];
  onSelectSingle: (option: ReservationQuestionOptionDto) => void;
  onSelectMultiple: (option: ReservationQuestionOptionDto) => void;
  title?: string;
  currentQuestion: ReservationQuestionDto;
  currentQuestionSlug?: EventQuestionSlug;
  customInputValue?: string;
  onCustomInputChange?: (value: string) => void;
}

export function EventAnswerOptionsList({
  currentQuestion,
  selectedOptions,
  onSelectSingle,
  onSelectMultiple,
  title,
  customInputValue,
  onCustomInputChange,
}: EventAnswerOptionsListProps) {
  const theme = useMantineTheme();

  const questionType = currentQuestion.type;
  const options: ReservationQuestionOptionDto[] = currentQuestion?.options ?? [];

  const handleOptionClick = (option: ReservationQuestionOptionDto) => {
    if (questionType === "select_single") {
      onSelectSingle(option);
    } else if (questionType === "select_multiple") {
      onSelectMultiple(option);
    }
  };

  const isSelected = (optionValue: string): boolean => {
    if (questionType === "select_single") {
      return selectedOptions === optionValue;
    }
    if (questionType === "select_multiple" && Array.isArray(selectedOptions)) {
      return selectedOptions.includes(optionValue);
    }
    return false;
  };

  const numOptions = options.length;
  const averageOptionLength =
    options.reduce((sum, opt) => sum + opt.displayText.length, 0) / (numOptions || 1);

  let cols = 1;
  if (numOptions > 2) {
    if (averageOptionLength <= 10 && numOptions > 4) {
      cols = 2;
    }
  }
  if (numOptions > 6 && averageOptionLength <= 8) {
    cols = 3;
  }

  return (
    <Stack align="center" w="100%">
      {title && (
        <Text size="lg" fw={500} ta="center">
          {title}
        </Text>
      )}
      <SimpleGrid cols={cols} spacing="md" w="100%">
        {options.map((option) => (
          <Stack key={option.value} gap="xs" w="100%">
            <Button
              variant={isSelected(option.value) ? "filled" : "outline"}
              onClick={() => handleOptionClick(option)}
              color={isSelected(option.value) ? theme.colors.yellow[0] : "gray"}
              style={{
                color: isSelected(option.value) ? "black" : theme.white,
                height: "auto",
                padding: "10px 15px",
                whiteSpace: "normal",
                lineHeight: "1.4",
              }}
              fullWidth
              radius="md"
              styles={{
                label: {
                  whiteSpace: "normal",
                  wordWrap: "break-word",
                },
              }}
            >
              <Text size="md" ta="center">
                {option.displayText}
              </Text>
            </Button>
            {option.customInputType === "text_input" && isSelected(option.value) && (
              <Textarea
                placeholder="구체적으로 알려주세요"
                value={customInputValue || ""}
                onChange={(e) => onCustomInputChange?.(e.currentTarget.value)}
                styles={{
                  input: {
                    backgroundColor: theme.black,
                    color: theme.white,
                  },
                }}
              />
            )}
          </Stack>
        ))}
      </SimpleGrid>
    </Stack>
  );
}
